
/**
 * Alpine.js wrapper component for creating a rich card carousel.
 * Features include:
 * - Initializing and destroying the carousel.
 * - Building the carousel swiper with autoplay and navigation controls.
 * - Adjusting autoplay based on window width.
 * - Pausing and resuming autoplay and status bar animation on mouse events.
 * - Returns object containing methods and properties for managing the rich card carousel.
 */

function richCardCarousel() {
  return {
    swiper: null,

    init() {
      this.cacheNodes();
      this.buildSwiper();
      this.addResizeListener();
    },

    destroy() {
      this.removeResizeListener();
      if (this.swiper) {
        this.swiper.destroy();
      }
    },

    addResizeListener() {
      this.resizeListener = () => {
        this.adjustAutoplay();
      };
      window.addEventListener('resize', this.resizeListener);
    },

    removeResizeListener() {
      window.removeEventListener('resize', this.resizeListener);
    },

    cacheNodes() {
      this.swiperContainer = document.querySelector('.rich-card-carousel__container');
    },

    buildSwiper() {
      if (this.swiper || !this.swiperContainer) {
        return;
      }
      this.swiper = new Swiper(this.swiperContainer, {
        slidesPerView: 'auto',
        centeredSlides: false,
        autoplay: {
          delay: 6000,
          disableOnInteraction: false,
        },
        navigation: {
          nextEl: this.swiperContainer.querySelector('.swiper-button-next-rich-card'),
          prevEl: this.swiperContainer.querySelector('.swiper-button-prev-rich-card'),
        },
        breakpoints: {
          768: {
            centeredSlides: true,
          },
        },
        on: {
          init: () => {
            // Start the status bar animation for the first slide
            this.startStatusBarAnimation();
          },
          slideChangeTransitionStart: () => {
            // Reset and start status bar animation on slide change
            this.resetStatusBarAnimation();
            this.startStatusBarAnimation();
          },
          slideChangeTransitionEnd: () => {
            // Optional: any cleanup if needed when the slide transition ends
          },
        },
      });

      window.addEventListener('resize', () => {
        this.adjustAutoplay();
      });

      this.swiperContainer.addEventListener('mouseenter', this.pauseAutoplayAndStatusBar.bind(this));
      this.swiperContainer.addEventListener('mouseover', this.pauseAutoplayAndStatusBar.bind(this));
      this.swiperContainer.addEventListener('mouseleave', this.resumeAutoplayAndStatusBar.bind(this));
      this.reviewSwiperLoaded = true;
    },

    pauseAutoplayAndStatusBar() {
      if (this.swiper) {
        this.swiper.autoplay.stop();
        this.pauseStatusBarAnimation();
      }
    },

    pauseStatusBarAnimation() {
      let currentStatusBar = this.swiperContainer.querySelector('.swiper-slide-active .rich-card-carousel__status-bar');
      if (currentStatusBar) {
        currentStatusBar.style.width = `${currentStatusBar.clientWidth}px`;
      }
    },

    adjustAutoplay() {
      if (window.innerWidth >= 768) {
        this.swiper.autoplay.start();
      } else {
        this.swiper.autoplay.stop();
      }
    },

    resumeAutoplayAndStatusBar() {
      if (this.swiper) {
        this.swiper.autoplay.start();
        this.startStatusBarAnimation();
      }
    },

    startStatusBarAnimation() {
      let currentStatusBar = this.swiperContainer.querySelector('.swiper-slide-active .rich-card-carousel__status-bar');
      if (currentStatusBar) {
        setTimeout(() => {
          currentStatusBar.style.width = '100%';
        });
      }
    },

    resetStatusBarAnimation() {
      let statusBars = this.swiperContainer.querySelectorAll('.rich-card-carousel__status-bar');
      statusBars.forEach((bar) => {
        bar.style.width = '0%';
      });
    },
  };
}

document.addEventListener('alpine:init', () => {
  Alpine.data('richCardCarousel', richCardCarousel);
});
